// src/web/registration.ts
import {
  startRegistration
} from "@simplewebauthn/browser";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

// src/utils/password.ts
import { Aes128Gcm, CipherSuite, DhkemP256HkdfSha256, HkdfSha256 } from "@hpke/core";
var RANDOM_SIZE = 32;
var generateRandomString = () => generateRandomStringLength(RANDOM_SIZE);
var generateRandomStringLength = (length) => [...globalThis.crypto.getRandomValues(new Uint8Array(length))].map((b) => b.toString(16).padStart(2, "0")).join("");
var base64ToArrayBuffer = (b64) => {
  const bin = atob(b64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return bytes.buffer;
};
async function generateECDHKeyPair() {
  const { subtle } = globalThis.crypto;
  const keyPair = await subtle.generateKey(
    {
      name: "ECDH",
      namedCurve: "P-256"
    },
    false,
    ["deriveKey", "deriveBits"]
  );
  return keyPair;
}
var getChallengePartFromfKey = (key) => !key.x || !key.y ? "00000000000000000000000000000000" : key.x.slice(key.x.length - 8) + key.y.slice(key.y.length - 8);
var generatePasskeyChallenge = (step, serverChallenge, clientPubKey, serverPubKey) => `${step}:${serverChallenge}:${getChallengePartFromfKey(clientPubKey)}:${getChallengePartFromfKey(serverPubKey)}`;
var generatePasskeyChallengeForAuth = (step, serverChallenge, clientPubKey) => `${step}:${serverChallenge}:${getChallengePartFromfKey(clientPubKey)}`;
var sha256Base64 = async (message) => {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashString = String.fromCharCode(...hashArray);
  return toUrlSafeBase64(btoa(hashString));
};
var toUrlSafeBase64 = (base64) => base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
var verifyChallengeIntegrity = (challenge, clientPubKey, serverPubKey) => {
  const challengeParts = atob(challenge).split(":");
  if (challengeParts.length !== 4) {
    throw new Error("Invalid challenge");
  }
  if (challengeParts[2] !== getChallengePartFromfKey(clientPubKey) || challengeParts[3] !== getChallengePartFromfKey(serverPubKey)) {
    throw new Error("Invalid challenge");
  }
  return true;
};
var generateECDHSharedSecret = async (privateKey, otherPubKey) => {
  const { subtle } = globalThis.crypto;
  const sharedSecretKey = await subtle.deriveKey(
    {
      name: "ECDH",
      public: otherPubKey
    },
    privateKey,
    {
      name: "HKDF",
      hash: "SHA-256"
    },
    false,
    ["deriveBits"]
  );
  const aesKey = await subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      info: new Uint8Array([]),
      salt: new Uint8Array([])
    },
    sharedSecretKey,
    256
    // 256 bits = 32 bytes
  );
  const aesKeyArray = new Uint8Array(aesKey);
  return Array.from(aesKeyArray, (byte) => byte.toString(16).padStart(2, "0")).join("");
};
var hpkeSuite = new CipherSuite({
  kem: new DhkemP256HkdfSha256(),
  kdf: new HkdfSha256(),
  aead: new Aes128Gcm()
});
async function generateHpkeKeyPair() {
  return await hpkeSuite.kem.generateKeyPair();
}
var generatePasskeyChallengeForAuthHpke = (step, serverChallenge, clientPubKey) => {
  const challengePart = getChallengePartFromfKey(clientPubKey);
  return `${step}:${serverChallenge}:${challengePart}`;
};
var importHpkePublicKeyFromJwk = async (jwk) => await globalThis.crypto.subtle.importKey(
  "jwk",
  jwk,
  {
    name: "ECDH",
    namedCurve: "P-256"
  },
  true,
  []
);
var encryptWithHpke = async (data, recipientPublicKeyJwk) => {
  const recipientPublicKey = await importHpkePublicKeyFromJwk(recipientPublicKeyJwk);
  const sender = await hpkeSuite.createSenderContext({
    recipientPublicKey
  });
  const ct = await sender.seal(new TextEncoder().encode(data).buffer);
  const ciphertext = btoa(String.fromCharCode(...new Uint8Array(ct)));
  const encapsulatedKey = btoa(String.fromCharCode(...new Uint8Array(sender.enc)));
  return JSON.stringify({
    ciphertext,
    encapsulatedKey
  });
};
var decryptWithHpke = async (encryptedData, recipientPrivateKey) => {
  const { ciphertext, encapsulatedKey } = JSON.parse(encryptedData);
  const ct = base64ToArrayBuffer(ciphertext);
  const enc = base64ToArrayBuffer(encapsulatedKey);
  const recipient = await hpkeSuite.createRecipientContext({
    recipientKey: recipientPrivateKey,
    enc
  });
  const pt = await recipient.open(ct);
  return new TextDecoder().decode(pt);
};

// src/web/registration.ts
var requestRegistrationOptions = async ({
  apiUrl,
  userId
}) => {
  const registrationOptionsResponse = await fetch(`${apiUrl}/registration-options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      userId
    })
  });
  const { options, serverPubKey } = await registrationOptionsResponse.json();
  return { options, serverPubKey };
};
var verifyRegistration = async ({
  apiUrl,
  registrationResponse,
  userId,
  clientPubKey
}) => {
  const verificationResponse = await fetch(`${apiUrl}/verify-registration`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      userId,
      registrationResponse,
      clientPubKey
    })
  });
  const response = await verificationResponse.json();
  return response;
};
var usePasskeyRegistration = ({ apiUrl }) => {
  const [transientKeyPair, setTransientKeyPair] = useState(null);
  const register = async () => {
    const keyPair = await generateECDHKeyPair();
    setTransientKeyPair(keyPair);
    const userId = uuidv4();
    try {
      const clientPubKey = await globalThis.crypto.subtle.exportKey(
        "jwk",
        keyPair.publicKey
      );
      const registrationOptions = await requestRegistrationOptions({
        apiUrl,
        userId
      });
      if (registrationOptions) {
        const challenge = await sha256Base64(
          generatePasskeyChallenge(
            "Register",
            registrationOptions.options.challenge,
            clientPubKey,
            registrationOptions.serverPubKey
          )
        );
        const registrationResponse = await startRegistration({
          optionsJSON: { ...registrationOptions.options, challenge }
        });
        const response = await verifyRegistration({
          registrationResponse,
          userId,
          apiUrl,
          clientPubKey
        });
        if (response.verified) {
          const serverkey = await globalThis.crypto.subtle.importKey(
            "jwk",
            registrationOptions.serverPubKey,
            { name: "ECDH", namedCurve: "P-256" },
            false,
            []
          );
          const passkey_kek = await generateECDHSharedSecret(keyPair.privateKey, serverkey);
          return {
            verified: true,
            userId,
            passkey_kek
          };
        } else {
          return {
            verified: false,
            userId: void 0,
            passkey_kek: void 0
          };
        }
      }
    } catch (error) {
      console.error(error);
      setTransientKeyPair(null);
      return {
        verified: false,
        userId: void 0,
        passkey_kek: void 0
      };
    }
  };
  useEffect(
    () => () => {
      setTransientKeyPair(null);
    },
    []
  );
  return { register, transientKeyPair };
};

// src/web/authentication.ts
import {
  startAuthentication
} from "@simplewebauthn/browser";
var verifyAuthentication = async ({
  apiUrl,
  authenticationResponse,
  userId,
  clientPubKey,
  keyRotationStatus
}) => {
  const verificationResponse = await fetch(`${apiUrl}/verify-authentication`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      userId,
      authenticationResponse,
      clientPubKey,
      keyRotationStatus
    })
  });
  const response = await verificationResponse.json();
  return response;
};
var requestAuthenticationOptions = async ({
  apiUrl,
  userId
}) => {
  const registrationOptionsResponse = await fetch(`${apiUrl}/authentication-options`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      userId
    })
  });
  const { options } = await registrationOptionsResponse.json();
  return options;
};
var authenticatePasskey = async ({
  apiUrl,
  userId,
  keyRotationStatus = "Inactive"
}) => {
  try {
    if (!userId) {
      throw new Error("User ID not found");
    }
    const clientKeyPair = await generateHpkeKeyPair();
    const clientPubKey = await globalThis.crypto.subtle.exportKey(
      "jwk",
      clientKeyPair.publicKey
    );
    const authenticationOptions = await requestAuthenticationOptions({
      apiUrl,
      userId
    });
    if (authenticationOptions) {
      const challenge = await sha256Base64(
        generatePasskeyChallengeForAuthHpke("Login", authenticationOptions.challenge, clientPubKey)
      );
      const authenticationResponse = await startAuthentication({
        optionsJSON: { ...authenticationOptions, challenge }
      });
      const verificationResult = await verifyAuthentication({
        authenticationResponse,
        userId,
        apiUrl,
        clientPubKey,
        keyRotationStatus
      });
      if (verificationResult.verified && verificationResult.passkey_kek) {
        const decrypted = await decryptWithHpke(
          verificationResult.passkey_kek,
          clientKeyPair.privateKey
        );
        const parsed = JSON.parse(decrypted);
        const expected = await sha256Base64(`${parsed.old}|${parsed.next ?? ""}`);
        const checksumVerified = expected === parsed.checksum;
        if (!checksumVerified) {
          throw new Error("Checksum verification failed");
        }
        return {
          verified: true,
          passkey_kek: parsed.old,
          next_passkey_kek: parsed.next || void 0,
          checksumVerified
        };
      }
    }
  } catch (error) {
    console.error(error);
  }
  return { verified: false, passkey_kek: "", checksumVerified: false };
};

// src/web/utils/browser.ts
var getIsPasskeySupported = async () => window.PublicKeyCredential.isConditionalMediationAvailable();

// src/server/registration.ts
import {
  generateRegistrationOptions,
  verifyRegistrationResponse
} from "@simplewebauthn/server";
import { isoUint8Array } from "@simplewebauthn/server/helpers";
var PASSKEY_ALGORITHMS_IDS = [-7, -257, -8];
var RANDOM_CHALLENGE_SIZE = 32;
var REGISTRATION_CHALLENGE_TIMEOUT_MS = 2 * 60 * 1e3;
var pendingRegistrations = /* @__PURE__ */ new Map();
var cleanupExpiredRegistrations = () => {
  const now = Date.now();
  const EXPIRY_TIME = 5 * 60 * 1e3;
  for (const [key, value] of pendingRegistrations.entries()) {
    if (now - value.timestamp > EXPIRY_TIME) {
      pendingRegistrations.delete(key);
    }
  }
};
var pendingRegistrationsCleanupInterval = null;
function startPendingRegistrationsCleanup() {
  if (!pendingRegistrationsCleanupInterval) {
    pendingRegistrationsCleanupInterval = setInterval(cleanupExpiredRegistrations, 60 * 1e3);
  }
}
function stopPendingRegistrationsCleanup() {
  if (pendingRegistrationsCleanupInterval) {
    clearInterval(pendingRegistrationsCleanupInterval);
    pendingRegistrationsCleanupInterval = null;
  }
}
var getRegistrationOptions = async ({
  userId,
  db,
  config
}) => {
  const created = await db.createUser(userId);
  if (!created) {
    console.log("User already exists");
    throw new Error("User already exists");
  }
  const passkeys = await db.getPasskeysByUserId({ userId });
  const serverKeyPair = await generateECDHKeyPair();
  const serverPubKeyJsw = await globalThis.crypto.subtle.exportKey("jwk", serverKeyPair.publicKey);
  const registrationKey = `${userId}-${Date.now()}-${Math.random()}`;
  const challenge = generateRandomStringLength(RANDOM_CHALLENGE_SIZE);
  pendingRegistrations.set(registrationKey, {
    serverPubKeyJsw,
    serverKeyPair,
    timestamp: Date.now()
  });
  const options = await generateRegistrationOptions({
    rpName: config.RP_NAME,
    rpID: config.RP_ID,
    userName: userId,
    userID: isoUint8Array.fromUTF8String(userId),
    challenge,
    attestationType: "none",
    excludeCredentials: passkeys.map((passkey) => ({
      id: passkey.id,
      transports: passkey.transports
    })),
    authenticatorSelection: {
      residentKey: "preferred",
      userVerification: "required"
    },
    supportedAlgorithmIDs: PASSKEY_ALGORITHMS_IDS,
    timeout: REGISTRATION_CHALLENGE_TIMEOUT_MS
  });
  await db.setRegistrationOptionsForUserId(userId, { ...options, registrationKey });
  return { options, serverPubKey: serverPubKeyJsw };
};
var verifyRegistration2 = async ({
  userId,
  registrationResponse,
  clientPubKey,
  db,
  config
}) => {
  try {
    const publicKey = registrationResponse.response.publicKey;
    const { options: currentOptions, createdAt } = await db.getRegistrationOptionsByUserId(userId);
    if (!currentOptions?.challenge || !currentOptions.registrationKey) {
      throw new Error("No registration options found for user");
    }
    if (!createdAt) {
      throw new Error("No registration options timestamp found for user");
    }
    const isChallengeExpired = Date.now() - new Date(createdAt).getTime() > REGISTRATION_CHALLENGE_TIMEOUT_MS;
    if (isChallengeExpired) {
      throw new Error("Registration challenge expired");
    }
    const registrationData = currentOptions.registrationKey ? pendingRegistrations.get(currentOptions.registrationKey) : null;
    if (!registrationData) {
      throw new Error("Server key pair not found or expired");
    }
    const expectedChallenge = await sha256Base64(
      generatePasskeyChallenge(
        "Register",
        currentOptions.challenge,
        clientPubKey,
        registrationData.serverPubKeyJsw
      )
    );
    const verification = await verifyRegistrationResponse({
      response: registrationResponse,
      expectedChallenge,
      expectedOrigin: config.ORIGIN,
      expectedRPID: config.RP_ID
    });
    const { verified, registrationInfo } = verification;
    if (verified && registrationInfo) {
      const clientKey = await globalThis.crypto.subtle.importKey(
        "jwk",
        clientPubKey,
        { name: "ECDH", namedCurve: "P-256" },
        false,
        []
      );
      const passkey_kek = await generateECDHSharedSecret(
        registrationData.serverKeyPair.privateKey,
        clientKey
      );
      await db.setPasskeyKekForUserId(userId, passkey_kek);
      await db.setUserBindingSignatureForUserId(userId, publicKey);
      await registerNewPasskeyForUserId({
        userId,
        registrationInfo,
        db,
        publicKeyStr: publicKey
      });
      if (currentOptions.registrationKey) {
        pendingRegistrations.delete(currentOptions.registrationKey);
      }
      return { verified, publicKey };
    } else {
      throw new Error("Registration verification failed");
    }
  } catch (e) {
    console.error(e);
    throw e;
  } finally {
    await db.deleteRegistrationOptionsForUserId(userId);
  }
};
var registerNewPasskeyForUserId = async ({
  userId,
  registrationInfo,
  db,
  publicKeyStr
}) => {
  const { credential, credentialDeviceType, credentialBackedUp } = registrationInfo;
  const passkey = {
    userId,
    webAuthnUserID: userId,
    id: credential.id,
    publicKey: credential.publicKey,
    publicKeyStr,
    counter: credential.counter,
    transports: credential.transports,
    deviceType: credentialDeviceType,
    backedUp: credentialBackedUp
  };
  await db.addPasskeyForUserId({ userId, passkey });
};

// src/server/authentication.ts
import {
  generateAuthenticationOptions,
  verifyAuthenticationResponse
} from "@simplewebauthn/server";
var AUTHENTICATION_CHALLENGE_TIMEOUT_MS = 2 * 60 * 1e3;
var getAuthenticationOptions = async ({
  userId,
  db,
  config
}) => {
  const passkeys = await db.getPasskeysByUserId({ userId });
  const options = await generateAuthenticationOptions({
    rpID: config.RP_ID,
    allowCredentials: passkeys.map((passkey) => ({
      id: passkey.id,
      transports: passkey.transports
    })),
    userVerification: "required",
    timeout: AUTHENTICATION_CHALLENGE_TIMEOUT_MS
  });
  await db.setAuthenticationOptionsForUserId(userId, options);
  return { options };
};
var verifyAuthentication2 = async ({
  userId,
  authenticationResponse,
  clientPubKey,
  keyRotationStatus,
  db,
  config
}) => {
  try {
    const passkey = await db.getPasskeyById({ userId, passkeyId: authenticationResponse.id });
    if (!passkey) {
      throw new Error("No passkey found for user");
    }
    const { options: currentOptions, createdAt } = await db.getAuthenticationOptionsByUserId(userId);
    if (!currentOptions) {
      throw new Error("No authentication options found for user");
    }
    if (!createdAt) {
      throw new Error("No authentication options timestamp found for user");
    }
    const isChallengeExpired = Date.now() - new Date(createdAt).getTime() > AUTHENTICATION_CHALLENGE_TIMEOUT_MS;
    if (isChallengeExpired) {
      throw new Error("Authentication challenge expired");
    }
    const expectedChallenge = await sha256Base64(
      generatePasskeyChallengeForAuthHpke("Login", currentOptions.challenge, clientPubKey)
    );
    const verification = await verifyAuthenticationResponse({
      response: authenticationResponse,
      expectedChallenge,
      expectedOrigin: config.ORIGIN,
      expectedRPID: config.RP_ID,
      credential: {
        id: passkey.id,
        publicKey: passkey.publicKey,
        counter: passkey.counter,
        transports: passkey.transports
      }
    });
    const { verified } = verification;
    if (verified) {
      const verifiedBindingSignature = await db.verifyUserBindingSignatureForUserId(
        userId,
        passkey.publicKeyStr
      );
      if (!verifiedBindingSignature) {
        throw new Error("User binding signature verification failed");
      }
      if (keyRotationStatus === "Success") {
        await db.commitPasskeyKekRotationForUserId?.(userId);
      }
      const storedPasskeyKek = await db.getPasskeyKekForUserId(userId);
      if (!storedPasskeyKek) {
        throw new Error("No passkey KEK found for user");
      }
      let nextKek;
      if (keyRotationStatus !== "Success") {
        const meta = await db.getPasskeyKekMetaForUserId?.(userId);
        const now = Date.now();
        const currentExpired = !!meta?.passkey_kek_expires_at && new Date(meta.passkey_kek_expires_at).getTime() <= now;
        const nextExpired = !!meta?.passkey_kek_next_expires_at && new Date(meta.passkey_kek_next_expires_at).getTime() <= now;
        if (currentExpired) {
          if (!nextExpired) {
            nextKek = await db.getPasskeyKekNextForUserId?.(userId);
          }
          if (!nextKek) {
            const generated = generateRandomString();
            await db.setPasskeyKekNextForUserId?.(userId, generated);
            nextKek = generated;
          }
        } else {
          if (!nextExpired) {
            nextKek = await db.getPasskeyKekNextForUserId?.(userId);
          } else {
            const generated = generateRandomString();
            await db.setPasskeyKekNextForUserId?.(userId, generated);
            nextKek = generated;
          }
        }
      }
      const payload = {
        old: storedPasskeyKek.toString(),
        next: nextKek ?? "",
        checksum: await sha256Base64(`${storedPasskeyKek}|${nextKek ?? ""}`)
      };
      const encryptedBundle = await encryptWithHpke(JSON.stringify(payload), clientPubKey);
      return {
        verified,
        passkey_kek: encryptedBundle
      };
    } else {
      throw new Error("Authentication verification failed");
    }
  } catch (e) {
    console.error(e);
    throw e;
  } finally {
    await db.deleteAuthenticationOptionsForUserId(userId);
  }
};
export {
  authenticatePasskey,
  base64ToArrayBuffer,
  decryptWithHpke,
  encryptWithHpke,
  generateECDHKeyPair,
  generateECDHSharedSecret,
  generateHpkeKeyPair,
  generatePasskeyChallenge,
  generatePasskeyChallengeForAuth,
  generatePasskeyChallengeForAuthHpke,
  generateRandomString,
  generateRandomStringLength,
  getAuthenticationOptions,
  getChallengePartFromfKey,
  getIsPasskeySupported,
  getRegistrationOptions,
  importHpkePublicKeyFromJwk,
  sha256Base64,
  startPendingRegistrationsCleanup,
  stopPendingRegistrationsCleanup,
  toUrlSafeBase64,
  usePasskeyRegistration,
  verifyAuthentication2 as verifyAuthentication,
  verifyChallengeIntegrity,
  verifyRegistration2 as verifyRegistration
};
